import { Box, Flex, HStack, Text, VStack, chakra } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatShortTime } from '@/lib/format-short-time';

interface SystemEventCardProps {
  /** Glyph shown in the pastille. Decorative — the title carries the meaning. */
  icon: ReactNode;
  title: string;
  /** Values line: an enumeration, never a sentence. */
  details?: string;
  /** ISO timestamp of the fact, not of its recording. */
  occurredAt: string;
  /** Evidence attached to the marker (a mention thumbnail today). Decorative. */
  media?: ReactNode;
  /**
   * Makes the whole card the click target. A registry card never carries a
   * button of its own: either it opens something, or it is inert.
   */
  onOpen?: () => void;
}

const CARD_STYLE = {
  bg: 'babyPowder',
  borderWidth: '1px',
  borderColor: 'border',
  rounded: 'xl',
  px: '13px',
  py: '11px',
  w: 'full',
} as const;

/**
 * The registry entry of a conversation thread: a framed block that records a
 * fact and its time. Every marker name wears this same card — monochrome, no
 * per-type colour, no shadow, nothing that animates.
 */
const SystemEventCard = ({
  icon,
  title,
  details,
  occurredAt,
  media,
  onOpen,
}: SystemEventCardProps) => {
  const { i18n } = useTranslation();
  const time = formatShortTime(occurredAt, i18n?.language);

  const content = (
    <HStack align="flex-start" gap="10px" w="full">
      <Flex
        aria-hidden="true"
        boxSize="25px"
        rounded="md"
        bg="softCream"
        color="primary.900"
        align="center"
        justify="center"
        flexShrink={0}
      >
        {icon}
      </Flex>
      {media && (
        <Box
          aria-hidden="true"
          position="relative"
          boxSize="44px"
          rounded="md"
          overflow="hidden"
          bg="softCream"
          flexShrink={0}
        >
          {media}
        </Box>
      )}
      <VStack align="stretch" gap="2px" flex="1" minW={0}>
        <Text
          fontFamily="heading"
          fontWeight="semibold"
          fontSize="12.5px"
          lineHeight="1.3"
          color="primary.900"
        >
          {title}
        </Text>
        {details && (
          <Text fontSize="12px" lineHeight="1.45" color="navInactive" lineClamp={2}>
            {details}
          </Text>
        )}
      </VStack>
      {time && (
        <chakra.time dateTime={occurredAt} fontSize="12px" color="inkMuted" flexShrink={0} pt="1px">
          {time}
        </chakra.time>
      )}
    </HStack>
  );

  if (!onOpen) {
    return (
      <Box role="note" {...CARD_STYLE}>
        {content}
      </Box>
    );
  }

  return (
    <chakra.button
      type="button"
      onClick={onOpen}
      cursor="pointer"
      textAlign="left"
      _hover={{ bg: 'softCream' }}
      _focusVisible={{
        outlineWidth: '2px',
        outlineStyle: 'solid',
        outlineColor: 'primary.900',
        outlineOffset: '2px',
      }}
      {...CARD_STYLE}
    >
      {content}
    </chakra.button>
  );
};

export default SystemEventCard;
